// src/components/pages/CAPMembershipPage.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "styled-components";
import MembershipTemplate from "../templates/MembershipTemplate";
import TermsAcceptance from "../molecules/TermsAcceptance";
import PayPalSection from "../molecules/PayPalSection";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const CAPMembershipPage = () => {
  const theme = useTheme();
  const [accepted, setAccepted] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state for fetching membership
  const [subscriptionDetails, setSubscriptionDetails] = useState(null); // Holds subscription info
  const [error, setError] = useState(null); // Error state
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchMembership = async () => {
      if (!user) {
        setLoading(false);
        navigate("/challenge/signin", {
          replace: true,
          state: { from: "/cap-membership" },
        });
        return;
      }

      try {
        const membershipsRef = collection(db, "memberships");
        const q = query(
          membershipsRef,
          where("userId", "==", user.uid),
          where("membershipType", "==", "cap"),
          where("status", "==", "active")
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Assuming one active 'cap' membership per user
          const membershipData = querySnapshot.docs[0].data();

          // Extract necessary details from PayPal data
          const { paypalData, createdAt } = membershipData;

          // Safely extract PayPal subscription details
          const {
            subscriptionID = "N/A",
            startTime: rawStartTime,
            billingCycle = "Yearly",
            amount = 360.0,
            nextBillingTime: rawNextBillingTime,
          } = paypalData || {};

          const startTime = rawStartTime ? new Date(rawStartTime) : null;
          const nextBillingTime = rawNextBillingTime
            ? new Date(rawNextBillingTime)
            : null;

          setSubscriptionDetails({
            subscriptionID,
            startTime,
            billingCycle,
            amount,
            nextBillingTime,
            createdAt, // Include createdAt
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching membership:", err);
        setError(
          "Failed to fetch your membership details. Please try again later."
        );
        setLoading(false);
      }
    };

    fetchMembership();
  }, [user, navigate]);

  const handleAccept = () => {
    setAccepted(true);
  };

  const handleReject = () => {
    navigate("/challenge/");
  };

  const handlePurchaseSuccess = async (data) => {
    if (user) {
      try {
        const membershipsCollection = collection(db, "memberships");
        await addDoc(membershipsCollection, {
          userId: user.uid,
          membershipType: "cap",
          membershipName: "Creator's Alliance Program",
          status: "active",
          paypalData: data, // Store all relevant PayPal data
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        setPurchaseSuccess(true);
      } catch (error) {
        console.error("Error creating membership:", error);
        setError("Failed to create your membership. Please try again.");
      }
    }
  };

  const handleGoBack = () => {
    navigate("/challenge/");
  };

  const termsUrl =
    "https://www.kbean.com/kbean-creator-alliance-program-subscription-agreement/";
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const planId = process.env.REACT_APP_PAYPAL_PLAN_ID;

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return "N/A";
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  return (
    <MembershipTemplate>
      <Container maxWidth="md" sx={{ my: 4 }}>
        {loading ? (
          <Box textAlign="center" sx={{ mt: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading your membership details...
            </Typography>
          </Box>
        ) : error ? (
          <Box textAlign="center" sx={{ mt: 4 }}>
            <Alert severity="error">{error}</Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                mt: 2,
                backgroundColor: theme.colors.green,
                color: theme.colors.white,
                borderRadius: "50px",
                padding: "10px 30px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: theme.colors.darkGreen,
                },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : subscriptionDetails ? (
          // Display subscription details if already subscribed
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: "basic-sans",
                fontSize: "1.8rem",
                textAlign: "center",
              }}
            >
              Your Membership Details
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontFamily: "basic-sans", mb: 1 }}
              >
                <strong>Subscription ID:</strong>{" "}
                {subscriptionDetails.subscriptionID}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: "basic-sans", mb: 1 }}
              >
                <strong>Membership Created At:</strong>{" "}
                {formatDate(
                  subscriptionDetails.createdAt
                    ? subscriptionDetails.createdAt.toDate()
                    : null
                )}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: "basic-sans", mb: 1 }}
              >
                <strong>Billing Cycle:</strong>{" "}
                {subscriptionDetails.billingCycle}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontFamily: "basic-sans", mb: 1 }}
              >
                <strong>Amount Paid:</strong> $
                {subscriptionDetails.amount.toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoBack}
                sx={{
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  borderRadius: "50px",
                  padding: "10px 30px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: theme.colors.green,
                    boxShadow: "none",
                  },
                }}
              >
                Go Back to Challenges
              </Button>
            </Box>
          </Paper>
        ) : (
          // Existing flow: accept terms and subscribe
          <>
            {!accepted && !purchaseSuccess && (
              <Paper
                elevation={3}
                sx={{ p: 4, backgroundColor: "transparent" }}
              >
                <TermsAcceptance
                  termsUrl={termsUrl}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  theme={theme}
                />
              </Paper>
            )}

            {accepted && !purchaseSuccess && (
              <Box textAlign="center" sx={{ mt: 4 }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontFamily: "basic-sans",
                    fontSize: "1.8rem",
                  }}
                >
                  Purchase Your Subscription
                </Typography>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <PayPalSection
                  clientId={clientId}
                  planId={planId}
                  onSuccess={handlePurchaseSuccess}
                />
              </Box>
            )}

            {purchaseSuccess && (
              <Box textAlign="center" sx={{ mt: 4, fontFamily: "basic-sans" }}>
                <Typography variant="h4" gutterBottom>
                  Subscription Successful!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 2, fontFamily: "basic-sans" }}
                >
                  Your subscription has been activated.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleGoBack}
                  sx={{
                    backgroundColor: theme.colors.blue,
                    color: theme.colors.white,
                    borderRadius: "50px",
                    padding: "10px 30px",
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: theme.colors.green,
                      boxShadow: "none",
                    },
                  }}
                >
                  Go Back to Challenges
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </MembershipTemplate>
  );
};

export default CAPMembershipPage;
